import React, { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "../auth";
import { useSettings } from "../settings";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { api } from "../api";
import {
  Image as ImageIcon,
  Newspaper,
  Layers,
  Mail,
  LogOut,
  Trash2,
  Plus,
  Save,
  Upload,
  LayoutDashboard,
  Settings as SettingsIcon,
  KeyRound,
  ExternalLink,
  FileText,
  Package,
  Building2,
  X,
} from "lucide-react";

const fileToBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

/* ---------- Dashboard ---------- */
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [news, setNews] = useState([]);
  useEffect(() => {
    api.stats().then(setStats).catch(() => {});
    api.listContacts().then((c) => setContacts(c.slice(0, 5))).catch(() => {});
    api.news().then((n) => setNews(n.slice(0, 4))).catch(() => {});
  }, []);

  const cards = [
    { label: "Haberler", value: stats?.news ?? "…", to: "/admin/news", icon: Newspaper, color: "from-[#C9A24B]/20 to-[#C9A24B]/5" },
    { label: "Sektörler", value: stats?.sectors ?? "…", to: "/admin/sectors", icon: Layers, color: "from-blue-500/15 to-blue-500/5" },
    { label: "Galeri Görseli", value: stats?.hero_images ?? "…", to: "/admin/hero", icon: ImageIcon, color: "from-emerald-500/15 to-emerald-500/5" },
    { label: "Form Mesajı", value: stats?.contacts ?? "…", to: "/admin/contacts", icon: Mail, color: "from-rose-500/15 to-rose-500/5" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <NavLink
            key={c.label}
            to={c.to}
            className={`relative overflow-hidden rounded-2xl border border-neutral-100 bg-white p-5 hover:border-[#C9A24B] transition-colors group`}
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${c.color} opacity-60 pointer-events-none`} />
            <div className="relative">
              <c.icon className="w-5 h-5 text-neutral-500" />
              <div className="mt-3 text-3xl font-black text-[#1a1a1a]">{c.value}</div>
              <div className="text-xs text-neutral-600 mt-1">{c.label}</div>
            </div>
          </NavLink>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-[#1a1a1a]">Son Form Mesajları</h3>
            <NavLink to="/admin/contacts" className="text-xs font-semibold text-[#8F6E1E] hover:underline">
              Tümü
            </NavLink>
          </div>
          {contacts.length === 0 && <div className="text-sm text-neutral-500">Henüz mesaj yok.</div>}
          <ul className="space-y-3">
            {contacts.map((c) => (
              <li key={c.id} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#C9A24B]/15 flex items-center justify-center text-[#8F6E1E] text-sm font-bold">
                  {c.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#1a1a1a] truncate">{c.name}</div>
                  <div className="text-xs text-neutral-500 truncate">{c.message}</div>
                </div>
                <div className="text-[11px] text-neutral-400 shrink-0">
                  {new Date(c.created_at).toLocaleDateString()}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-[#1a1a1a]">Son Haberler</h3>
            <NavLink to="/admin/news" className="text-xs font-semibold text-[#8F6E1E] hover:underline">
              Tümü
            </NavLink>
          </div>
          <ul className="space-y-3">
            {news.map((n) => (
              <li key={n.id} className="flex items-center gap-3">
                <img src={n.image} alt="" className="w-12 h-12 rounded-md object-cover grayscale" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-[#1a1a1a] truncate">{n.title}</div>
                  <div className="text-[11px] text-[#8F6E1E]">{n.date}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <a
        href="/"
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#1a1a1a] hover:text-[#C9A24B]"
      >
        Siteyi yeni sekmede aç <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
};

/* ---------- News ---------- */
const emptyNews = { title: "", date: "", excerpt: "", image: "", content: "", attachments: [] };
const NewsPanel = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyNews);

  const reload = () => api.news().then(setItems);
  useEffect(() => { reload(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    setForm((s) => ({ ...s, image: b64 }));
  };

  const onAttachment = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const b64s = await Promise.all(files.map(fileToBase64));
    setForm((s) => ({ ...s, attachments: [...(s.attachments || []), ...b64s] }));
    e.target.value = "";
  };

  const removeAttachment = (i) => {
    setForm((s) => ({ ...s, attachments: (s.attachments || []).filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    if (!form.title || !form.date || !form.image || !form.excerpt) {
      toast({ title: "Başlık, tarih, özet ve kapak görseli zorunlu" });
      return;
    }
    try {
      const payload = { ...form, attachments: form.attachments || [] };
      if (editing) {
        await api.updateNews(editing, payload);
        toast({ title: "Haber güncellendi" });
      } else {
        await api.createNews(payload);
        toast({ title: "Haber eklendi" });
      }
      setForm(emptyNews);
      setEditing(null);
      reload();
    } catch (e2) {
      toast({ title: "Hata", description: e2?.response?.data?.detail || "" });
    }
  };

  const edit = (n) => {
    setEditing(n.id);
    setForm({
      title: n.title,
      date: n.date,
      excerpt: n.excerpt,
      image: n.image,
      content: n.content || "",
      attachments: n.attachments || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Bu haberi silmek istediğinize emin misiniz?")) return;
    await api.deleteNews(id);
    toast({ title: "Haber silindi" });
    reload();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg text-[#1a1a1a]">
          {editing ? "Haberi Düzenle" : "Yeni Haber"}
        </h3>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <Label>Başlık</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Tarih (metin)</Label>
            <Input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" placeholder="12 Haziran 2025" />
          </div>
          <div className="md:col-span-2">
            <Label>Özet (kart üzerinde görünür)</Label>
            <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label>Detay İçeriği (haber sayfasında gösterilir)</Label>
            <Textarea
              rows={8}
              value={form.content || ""}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="mt-1.5"
              placeholder="Haberin tam metnini buraya yazın. Satır aralarına boşluk bırakabilirsiniz."
            />
          </div>
          <div className="md:col-span-2">
            <Label>Kapak Görseli</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                <Upload className="w-4 h-4" /> Yükle
                <input type="file" accept="image/*" onChange={onFile} className="hidden" />
              </label>
              <Input
                value={form.image?.startsWith("data:") ? "" : form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                placeholder="veya görsel URL'si yapıştırın"
                className="flex-1"
              />
              {form.image && <img src={form.image} alt="" className="w-16 h-16 object-cover rounded" />}
            </div>
          </div>

          <div className="md:col-span-2">
            <Label>Ek Dosyalar (PDF/Görsel — birden fazla eklenebilir)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                <Upload className="w-4 h-4" /> Dosya Ekle
                <input type="file" accept="image/*,application/pdf" multiple onChange={onAttachment} className="hidden" />
              </label>
              <span className="text-xs text-neutral-500">{(form.attachments || []).length} dosya</span>
            </div>
            {(form.attachments || []).length > 0 && (
              <div className="mt-3 grid sm:grid-cols-3 gap-2">
                {form.attachments.map((a, i) => (
                  <div key={i} className="relative rounded-lg border border-neutral-200 p-2 flex items-center gap-2">
                    {a.startsWith("data:application/pdf") ? (
                      <FileText className="w-6 h-6 text-[#4E0D1C]" />
                    ) : (
                      <img src={a} alt="" className="w-10 h-10 object-cover rounded" />
                    )}
                    <span className="text-xs text-neutral-600 truncate flex-1">Ek {i + 1}</span>
                    <button onClick={() => removeAttachment(i)} className="text-red-600 hover:text-red-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <Button onClick={save} className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full">
            <Save className="w-4 h-4 mr-2" /> {editing ? "Güncelle" : "Ekle"}
          </Button>
          {editing && (
            <Button variant="outline" onClick={() => { setEditing(null); setForm(emptyNews); }} className="rounded-full">
              İptal
            </Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((n) => (
          <div key={n.id} className="bg-white rounded-2xl border border-neutral-100 p-4 flex gap-4">
            <img src={n.image} alt={n.title} className="w-24 h-24 object-cover rounded-md grayscale" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#8F6E1E] font-semibold">{n.date}</div>
              <div className="font-bold text-[#1a1a1a] truncate">{n.title}</div>
              <p className="text-sm text-neutral-600 line-clamp-2">{n.excerpt}</p>
              <div className="text-[11px] text-neutral-500 mt-1">{(n.attachments || []).length} ek • {n.content ? "İçerik var" : "İçerik yok"}</div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => edit(n)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200">Düzenle</button>
                <button onClick={() => del(n.id)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100">
                  <Trash2 className="w-3 h-3 inline" /> Sil
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Sectors ---------- */
const SectorsPanel = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", subtitle: "", description: "", image: "" });
  const reload = () => api.sectors().then(setItems);
  useEffect(() => { reload(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    setForm((s) => ({ ...s, image: b64 }));
  };
  const startEdit = (s) => { setEditing(s.id); setForm({ title: s.title, subtitle: s.subtitle, description: s.description, image: s.image }); };
  const save = async () => {
    try {
      await api.updateSector(editing, form);
      toast({ title: "Sektör güncellendi" });
      setEditing(null);
      reload();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "" }); }
  };

  return (
    <div className="space-y-4">
      {editing && (
        <div className="bg-white rounded-2xl border border-neutral-100 p-6">
          <h3 className="font-black text-lg">Sektörü Düzenle</h3>
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Alt Başlık</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Açıklama</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
            <div className="md:col-span-2">
              <Label>Görsel</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                  <Upload className="w-4 h-4" /> Yükle
                  <input type="file" accept="image/*" onChange={onFile} className="hidden" />
                </label>
                <Input value={form.image?.startsWith("data:") ? "" : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="flex-1" placeholder="veya URL" />
                {form.image && <img src={form.image} alt="" className="w-16 h-16 object-cover rounded" />}
              </div>
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <Button onClick={save} className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full"><Save className="w-4 h-4 mr-2" /> Kaydet</Button>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">İptal</Button>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
            <div className="aspect-[4/3] bg-neutral-100"><img src={s.image} alt="" className="w-full h-full object-cover grayscale" /></div>
            <div className="p-4">
              <div className="font-black text-[#1a1a1a]">{s.title}</div>
              <div className="text-xs text-[#8F6E1E]">{s.subtitle}</div>
              <p className="text-sm text-neutral-600 mt-2 line-clamp-3">{s.description}</p>
              <button onClick={() => startEdit(s)} className="mt-3 text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200">Düzenle</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Hero images ---------- */
const HeroPanel = () => {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const reload = () => api.heroImages().then(setItems);
  useEffect(() => { reload(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const b64 = await fileToBase64(f);
    try { await api.addHeroImage(b64); toast({ title: "Görsel eklendi" }); reload(); }
    catch { toast({ title: "Hata" }); }
  };
  const addUrl = async () => {
    if (!url) return;
    await api.addHeroImage(url); setUrl(""); toast({ title: "Görsel eklendi" }); reload();
  };
  const del = async (id) => {
    if (!window.confirm("Bu görseli silmek istiyor musunuz?")) return;
    await api.deleteHeroImage(id); reload();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg">Yeni Görsel Ekle</h3>
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
            <Upload className="w-4 h-4" /> Cihazdan Yükle
            <input type="file" accept="image/*" onChange={onFile} className="hidden" />
          </label>
          <span className="text-sm text-neutral-500">veya</span>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." className="flex-1 min-w-[250px]" />
          <Button onClick={addUrl} className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" /> URL Ekle
          </Button>
        </div>
        <p className="mt-3 text-xs text-neutral-500">Not: Kaydırmalı galeri için en az 14 görsel kullanmanız önerilir.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {items.map((i) => (
          <div key={i.id} className="relative group rounded-lg overflow-hidden aspect-[3/4] bg-neutral-100">
            <img src={i.url} alt="" className="w-full h-full object-cover grayscale" />
            <button onClick={() => del(i.id)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-red-500 hover:text-white text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Contacts ---------- */
const ContactsPanel = () => {
  const [items, setItems] = useState([]);
  const reload = () => api.listContacts().then(setItems);
  useEffect(() => { reload(); }, []);
  const del = async (id) => {
    if (!window.confirm("Bu mesajı silmek istiyor musunuz?")) return;
    await api.deleteContact(id); reload();
  };
  if (items.length === 0) return <div className="text-neutral-500 text-sm">Henüz form mesajı yok.</div>;
  return (
    <div className="space-y-3">
      {items.map((c) => (
        <div key={c.id} className="bg-white rounded-xl border border-neutral-100 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-bold text-[#1a1a1a]">{c.name}</div>
              <div className="text-xs text-neutral-500">{c.email} · {c.phone || "—"}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-neutral-400">{new Date(c.created_at).toLocaleString()}</span>
              <button onClick={() => del(c.id)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100">
                <Trash2 className="w-3 h-3 inline" /> Sil
              </button>
            </div>
          </div>
          <p className="mt-3 text-neutral-700 text-[15px] leading-relaxed whitespace-pre-wrap">{c.message}</p>
        </div>
      ))}
    </div>
  );
};

/* ---------- Site Settings ---------- */
const SiteSettingsPanel = () => {
  const { settings, reload } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const upFile = async (e, key) => {
    const f = e.target.files?.[0]; if (!f) return;
    const b64 = await fileToBase64(f);
    setForm((s) => ({ ...s, [key]: b64 }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings({
        brand_name: form.brand_name,
        brand_suffix: form.brand_suffix,
        legal_name: form.legal_name,
        tagline: form.tagline,
        founded: Number(form.founded) || 1993,
        logo_url: form.logo_url || "",
        favicon_url: form.favicon_url || "",
        phone: form.phone,
        whatsapp: form.whatsapp || "",
        email: form.email,
        address: form.address,
        map_embed_url: form.map_embed_url || "",
        socials: form.socials || {},
      });
      await reload();
      toast({ title: "Ayarlar kaydedildi" });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const setSocial = (k, v) =>
    setForm((s) => ({ ...s, socials: { ...(s.socials || {}), [k]: v } }));

  return (
    <div className="space-y-6">
      {/* Brand & Logo */}
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg text-[#1a1a1a]">Marka & Logo</h3>
        <p className="text-sm text-neutral-500 mt-1">Logo ve favicon site genelinde otomatik güncellenir.</p>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div>
            <Label>Logo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl bg-neutral-50 border border-dashed border-neutral-300 flex items-center justify-center overflow-hidden">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
                ) : (
                  <span className="text-xs text-neutral-400">Logo yok</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                  <Upload className="w-4 h-4" /> Cihazdan Yükle
                  <input type="file" accept="image/*" onChange={(e) => upFile(e, "logo_url")} className="hidden" />
                </label>
                <Input
                  value={form.logo_url?.startsWith("data:") ? "" : form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  placeholder="veya logo URL'si"
                />
                {form.logo_url && (
                  <button onClick={() => setForm({ ...form, logo_url: "" })} className="text-xs text-red-600 hover:underline">Logoyu kaldır</button>
                )}
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-2">Öneri: PNG, şeffaf arka planlı, 400×120 px.</p>
          </div>

          <div>
            <Label>Favicon</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl bg-neutral-50 border border-dashed border-neutral-300 flex items-center justify-center overflow-hidden">
                {form.favicon_url ? (
                  <img src={form.favicon_url} alt="favicon" className="w-10 h-10 object-contain" />
                ) : (
                  <span className="text-xs text-neutral-400">Favicon yok</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                  <Upload className="w-4 h-4" /> Cihazdan Yükle
                  <input type="file" accept="image/*,.ico" onChange={(e) => upFile(e, "favicon_url")} className="hidden" />
                </label>
                <Input
                  value={form.favicon_url?.startsWith("data:") ? "" : form.favicon_url}
                  onChange={(e) => setForm({ ...form, favicon_url: e.target.value })}
                  placeholder="veya favicon URL'si"
                />
                {form.favicon_url && (
                  <button onClick={() => setForm({ ...form, favicon_url: "" })} className="text-xs text-red-600 hover:underline">Favicon'u kaldır</button>
                )}
              </div>
            </div>
            <p className="text-xs text-neutral-500 mt-2">Öneri: 64×64 veya 32×32 px, PNG/ICO.</p>
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div><Label>Marka Adı</Label><Input value={form.brand_name || ""} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Simge (®, ™)</Label><Input value={form.brand_suffix || ""} onChange={(e) => setForm({ ...form, brand_suffix: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Kuruluş Yılı</Label><Input type="number" value={form.founded || 0} onChange={(e) => setForm({ ...form, founded: e.target.value })} className="mt-1.5" /></div>
          <div className="md:col-span-2"><Label>Yasal Ad</Label><Input value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Slogan</Label><Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-1.5" /></div>
        </div>
      </div>

      {/* Contact info */}
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg text-[#1a1a1a]">İletişim Bilgileri</h3>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <div><Label>Telefon</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          <div><Label>E-posta</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          <div className="md:col-span-1"><Label>Adres</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
        </div>

        <div className="mt-4">
          <Label>WhatsApp Numarası (uluslararası format, ör: +905551112233)</Label>
          <Input
            value={form.whatsapp || ""}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
            placeholder="+905551112233"
            className="mt-1.5"
          />
          <p className="text-xs text-neutral-500 mt-1.5">Boş bırakırsanız WhatsApp butonu gizlenir. Numarayı ülke koduyla girin (Türkiye için +90...).</p>
        </div>

        <div className="mt-4">
          <Label>Harita Embed URL (Google Maps veya OpenStreetMap)</Label>
          <Textarea
            rows={2}
            value={form.map_embed_url || ""}
            onChange={(e) => setForm({ ...form, map_embed_url: e.target.value })}
            className="mt-1.5"
            placeholder={`Google Maps → Paylaş → Yerleştir → src="..." içindeki URL'i yapıştırın`}
          />
          <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
            Google Maps'te konumu açın → <b>Paylaş</b> → <b>Haritayı yerleştir</b> sekmesinden{" "}
            <b>HTML kodunu kopyala</b>. Yalnızca <code>src="…"</code> içindeki bağlantıyı buraya yapıştırın.
            <br />
            Örnek: <code className="break-all">https://www.google.com/maps/embed?pb=!1m18!1m12…</code>
          </p>
          {form.map_embed_url && (
            <div className="mt-3 rounded-lg overflow-hidden border border-neutral-100 aspect-[16/9] max-w-lg">
              <iframe
                title="Harita önizleme"
                src={form.map_embed_url}
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          )}
        </div>
      </div>

      {/* Socials */}
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg text-[#1a1a1a]">Sosyal Medya</h3>
        <p className="text-sm text-neutral-500 mt-1">Boş bırakılanlar footer'da gösterilmez.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {["facebook", "instagram", "youtube", "linkedin", "twitter"].map((k) => (
            <div key={k}>
              <Label className="capitalize">{k}</Label>
              <Input
                value={form.socials?.[k] || ""}
                onChange={(e) => setSocial(k, e.target.value)}
                placeholder={`https://${k}.com/...`}
                className="mt-1.5"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full px-6 h-11 shadow-lg"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Kaydediliyor…" : "Tüm Ayarları Kaydet"}
        </Button>
      </div>
    </div>
  );
};

/* ---------- Password ---------- */
const PasswordPanel = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (form.new_password.length < 6) return toast({ title: "Yeni şifre en az 6 karakter olmalı" });
    if (form.new_password !== form.confirm) return toast({ title: "Şifreler eşleşmiyor" });
    setSaving(true);
    try {
      await api.changePassword(form.current_password, form.new_password);
      toast({ title: "Şifre güncellendi" });
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg bg-white rounded-2xl border border-neutral-100 p-6">
      <h3 className="font-black text-lg text-[#1a1a1a]">Şifre Değiştir</h3>
      <div className="mt-4 space-y-4">
        <div><Label>Mevcut Şifre</Label><Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Yeni Şifre</Label><Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Yeni Şifre (Tekrar)</Label><Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5" /></div>
        <Button onClick={submit} disabled={saving} className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full">
          {saving ? "Kaydediliyor…" : "Şifreyi Güncelle"}
        </Button>
      </div>
    </div>
  );
};

/* ---------- Kurumsal İçerik ---------- */
const KurumsalPanel = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.kurumsal().then(setForm);
  }, []);

  if (!form) return <div className="text-neutral-500 text-sm">Yükleniyor…</div>;

  const setValueAt = (i, k, v) => {
    const arr = [...(form.values || [])];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, values: arr });
  };
  const addValue = () => setForm({ ...form, values: [...(form.values || []), { title: "", text: "" }] });
  const delValue = (i) => setForm({ ...form, values: (form.values || []).filter((_, idx) => idx !== i) });

  const setMileAt = (i, k, v) => {
    const arr = [...(form.milestones || [])];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, milestones: arr });
  };
  const addMile = () => setForm({ ...form, milestones: [...(form.milestones || []), { year: "", text: "" }] });
  const delMile = (i) => setForm({ ...form, milestones: (form.milestones || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      await api.updateKurumsal({
        story_title: form.story_title,
        story_p1: form.story_p1,
        story_p2: form.story_p2,
        stat_years: form.stat_years,
        stat_projects: form.stat_projects,
        stat_areas: form.stat_areas,
        mission_text: form.mission_text,
        vision_text: form.vision_text,
        values: (form.values || []).filter((v) => v.title || v.text),
        milestones: (form.milestones || []).filter((m) => m.year || m.text),
      });
      toast({ title: "Kurumsal içerik kaydedildi" });
    } catch (e) {
      toast({ title: "Hata", description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg text-[#1a1a1a]">Hikaye</h3>
        <div className="mt-4 space-y-4">
          <div><Label>Başlık</Label><Input value={form.story_title || ""} onChange={(e) => setForm({ ...form, story_title: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Paragraf 1</Label><Textarea rows={4} value={form.story_p1 || ""} onChange={(e) => setForm({ ...form, story_p1: e.target.value })} className="mt-1.5" /></div>
          <div><Label>Paragraf 2</Label><Textarea rows={4} value={form.story_p2 || ""} onChange={(e) => setForm({ ...form, story_p2: e.target.value })} className="mt-1.5" /></div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          <div><Label>Yıl (İstatistik)</Label><Input value={form.stat_years || ""} onChange={(e) => setForm({ ...form, stat_years: e.target.value })} className="mt-1.5" placeholder="30+" /></div>
          <div><Label>Proje</Label><Input value={form.stat_projects || ""} onChange={(e) => setForm({ ...form, stat_projects: e.target.value })} className="mt-1.5" placeholder="45+" /></div>
          <div><Label>Alan</Label><Input value={form.stat_areas || ""} onChange={(e) => setForm({ ...form, stat_areas: e.target.value })} className="mt-1.5" placeholder="6" /></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-100 p-6 grid md:grid-cols-2 gap-4">
        <div><Label>Misyon</Label><Textarea rows={5} value={form.mission_text || ""} onChange={(e) => setForm({ ...form, mission_text: e.target.value })} className="mt-1.5" /></div>
        <div><Label>Vizyon</Label><Textarea rows={5} value={form.vision_text || ""} onChange={(e) => setForm({ ...form, vision_text: e.target.value })} className="mt-1.5" /></div>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg text-[#1a1a1a]">Değerlerimiz</h3>
          <Button onClick={addValue} variant="outline" className="rounded-full h-9"><Plus className="w-4 h-4 mr-1" /> Ekle</Button>
        </div>
        <div className="mt-4 space-y-3">
          {(form.values || []).map((v, i) => (
            <div key={i} className="grid md:grid-cols-[1fr_2fr_auto] gap-2 items-start">
              <Input value={v.title} onChange={(e) => setValueAt(i, "title", e.target.value)} placeholder="Başlık (örn. Güven)" />
              <Input value={v.text} onChange={(e) => setValueAt(i, "text", e.target.value)} placeholder="Kısa açıklama" />
              <button onClick={() => delValue(i)} className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(!form.values || form.values.length === 0) && <div className="text-sm text-neutral-500">Henüz değer eklenmemiş.</div>}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg text-[#1a1a1a]">Tarihçe (Milestones)</h3>
          <Button onClick={addMile} variant="outline" className="rounded-full h-9"><Plus className="w-4 h-4 mr-1" /> Ekle</Button>
        </div>
        <div className="mt-4 space-y-3">
          {(form.milestones || []).map((m, i) => (
            <div key={i} className="grid md:grid-cols-[120px_1fr_auto] gap-2 items-start">
              <Input value={m.year} onChange={(e) => setMileAt(i, "year", e.target.value)} placeholder="Yıl (1993)" />
              <Input value={m.text} onChange={(e) => setMileAt(i, "text", e.target.value)} placeholder="Açıklama" />
              <button onClick={() => delMile(i)} className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(!form.milestones || form.milestones.length === 0) && <div className="text-sm text-neutral-500">Henüz milestone eklenmemiş.</div>}
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full px-6 h-11 shadow-lg">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Kaydediliyor…" : "Kaydet"}
        </Button>
      </div>
    </div>
  );
};

/* ---------- Documents ---------- */
const DocumentsPanel = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", file_url: "", file_type: "pdf", file_name: "" });

  const reload = () => api.documents().then(setItems);
  useEffect(() => { reload(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const b64 = await fileToBase64(f);
    const type = f.type.startsWith("image/") ? "image" : (f.type === "application/pdf" ? "pdf" : "other");
    setForm((s) => ({ ...s, file_url: b64, file_type: type, file_name: f.name, title: s.title || f.name.replace(/\.[^.]+$/, "") }));
  };

  const save = async () => {
    if (!form.title || !form.file_url) { toast({ title: "Başlık ve dosya zorunlu" }); return; }
    try {
      await api.createDocument(form);
      toast({ title: "Doküman eklendi" });
      setForm({ title: "", description: "", file_url: "", file_type: "pdf", file_name: "" });
      reload();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "" }); }
  };

  const del = async (id) => {
    if (!window.confirm("Bu dokümanı silmek istiyor musunuz?")) return;
    await api.deleteDocument(id); reload();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg text-[#1a1a1a]">Yeni Doküman</h3>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
          <div>
            <Label>Dosya (PDF veya Görsel)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                <Upload className="w-4 h-4" /> Dosya Seç
                <input type="file" accept="application/pdf,image/*" onChange={onFile} className="hidden" />
              </label>
              {form.file_name && <span className="text-xs text-neutral-600 truncate">{form.file_name}</span>}
            </div>
          </div>
          <div className="md:col-span-2"><Label>Açıklama</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
        </div>
        <div className="mt-5">
          <Button onClick={save} className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Ekle
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => (
          <div key={d.id} className="bg-white rounded-2xl border border-neutral-100 p-4 flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#4E0D1C]/10 text-[#4E0D1C] flex items-center justify-center shrink-0">
              {d.file_type === "pdf" ? <FileText className="w-6 h-6" /> : <ImageIcon className="w-6 h-6" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#1a1a1a] truncate">{d.title}</div>
              {d.description && <p className="text-xs text-neutral-500 line-clamp-2 mt-0.5">{d.description}</p>}
              <div className="mt-2 flex gap-2">
                <a href={d.file_url} target="_blank" rel="noreferrer" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200">Aç</a>
                <button onClick={() => del(d.id)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100">
                  <Trash2 className="w-3 h-3 inline" /> Sil
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-neutral-500 md:col-span-3">Henüz doküman eklenmemiş.</div>}
      </div>
    </div>
  );
};

/* ---------- Products ---------- */
const slugify = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const emptyProduct = {
  slug: "", title: "", subtitle: "", category: "Prefabrik Yapılar",
  description: "", cover_image: "", images: [], specs: [],
};

const ProductsPanel = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);

  const reload = () => api.products().then(setItems);
  useEffect(() => { reload(); }, []);

  const setCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const b64 = await fileToBase64(f);
    setForm((s) => ({ ...s, cover_image: b64 }));
  };
  const addImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const b64s = await Promise.all(files.map(fileToBase64));
    setForm((s) => ({ ...s, images: [...(s.images || []), ...b64s] }));
    e.target.value = "";
  };
  const removeImg = (i) => setForm((s) => ({ ...s, images: (s.images || []).filter((_, idx) => idx !== i) }));

  const setSpec = (i, k, v) => {
    const arr = [...(form.specs || [])];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, specs: arr });
  };
  const addSpec = () => setForm({ ...form, specs: [...(form.specs || []), { label: "", value: "" }] });
  const delSpec = (i) => setForm({ ...form, specs: (form.specs || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.title) { toast({ title: "Başlık zorunlu" }); return; }
    const payload = { ...form, slug: form.slug || slugify(form.title) };
    try {
      if (editing) {
        await api.updateProduct(editing, payload);
        toast({ title: "Ürün güncellendi" });
      } else {
        await api.createProduct(payload);
        toast({ title: "Ürün eklendi" });
      }
      setForm(emptyProduct); setEditing(null); reload();
    } catch (e) { toast({ title: "Hata", description: e?.response?.data?.detail || "" }); }
  };

  const edit = (p) => {
    setEditing(p.id);
    setForm({
      slug: p.slug, title: p.title, subtitle: p.subtitle || "", category: p.category || "Prefabrik Yapılar",
      description: p.description || "", cover_image: p.cover_image || "",
      images: p.images || [], specs: p.specs || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const del = async (id) => {
    if (!window.confirm("Bu ürünü silmek istiyor musunuz?")) return;
    await api.deleteProduct(id); reload();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-neutral-100 p-6">
        <h3 className="font-black text-lg text-[#1a1a1a]">{editing ? "Ürünü Düzenle" : "Yeni Ürün"}</h3>
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div>
            <Label>Başlık</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} className="mt-1.5" placeholder="Örn: Tek Katlı Prefabrik Yapı" />
          </div>
          <div>
            <Label>Slug (URL)</Label>
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="mt-1.5" placeholder="tek-katli-prefabrik-yapi" />
            <p className="text-[11px] text-neutral-500 mt-1">Ürün URL'sinde kullanılır: /urun/{form.slug || "..."}</p>
          </div>
          <div>
            <Label>Alt Başlık</Label>
            <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label>Kategori</Label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" placeholder="Prefabrik Yapılar / Çelik Yapılar / Konteynerler" />
          </div>
          <div className="md:col-span-2">
            <Label>Açıklama</Label>
            <Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" placeholder="Ürünün detaylı açıklaması..." />
          </div>

          <div>
            <Label>Kapak Görseli</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                <Upload className="w-4 h-4" /> Yükle
                <input type="file" accept="image/*" onChange={setCover} className="hidden" />
              </label>
              {form.cover_image && <img src={form.cover_image} alt="" className="w-14 h-14 object-cover rounded" />}
            </div>
          </div>

          <div>
            <Label>Ek Görseller (galeri)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-neutral-100 hover:bg-neutral-200 cursor-pointer text-sm font-medium">
                <Upload className="w-4 h-4" /> Görsel Ekle
                <input type="file" accept="image/*" multiple onChange={addImages} className="hidden" />
              </label>
              <span className="text-xs text-neutral-500">{(form.images || []).length} görsel</span>
            </div>
          </div>
        </div>

        {(form.images || []).length > 0 && (
          <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
            {form.images.map((img, i) => (
              <div key={i} className="relative aspect-square rounded overflow-hidden border border-neutral-200">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removeImg(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 text-red-600 hover:bg-red-500 hover:text-white flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6">
          <div className="flex items-center justify-between">
            <Label>Teknik Şartname</Label>
            <Button onClick={addSpec} variant="outline" className="rounded-full h-8"><Plus className="w-4 h-4 mr-1" /> Satır Ekle</Button>
          </div>
          <div className="mt-2 space-y-2">
            {(form.specs || []).map((s, i) => (
              <div key={i} className="grid md:grid-cols-[220px_1fr_auto] gap-2 items-center">
                <Input value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} placeholder="Örn: Ölçü" />
                <Input value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} placeholder="Örn: 3 x 6 m" />
                <button onClick={() => delSpec(i)} className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            {(!form.specs || form.specs.length === 0) && <div className="text-xs text-neutral-500">Henüz özellik eklenmemiş.</div>}
          </div>
        </div>

        <div className="mt-5 flex gap-3">
          <Button onClick={save} className="bg-[#1a1a1a] hover:bg-[#C9A24B] text-white rounded-full">
            <Save className="w-4 h-4 mr-2" /> {editing ? "Güncelle" : "Ekle"}
          </Button>
          {editing && (
            <Button variant="outline" onClick={() => { setEditing(null); setForm(emptyProduct); }} className="rounded-full">
              İptal
            </Button>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
            <div className="aspect-[4/3] bg-neutral-100">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="text-xs text-[#8F6E1E] font-semibold">{p.category}</div>
              <div className="font-black text-[#1a1a1a] mt-1">{p.title}</div>
              <div className="text-xs text-neutral-500 mt-0.5">/urun/{p.slug}</div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => edit(p)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200">Düzenle</button>
                <button onClick={() => del(p.id)} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100">
                  <Trash2 className="w-3 h-3 inline" /> Sil
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-neutral-500 lg:col-span-3">Henüz ürün eklenmemiş.</div>}
      </div>
    </div>
  );
};

/* ---------- Layout ---------- */
const Admin = () => {
  const { user, ready, logout } = useAuth();

  if (!ready) return <div className="min-h-screen flex items-center justify-center">Yükleniyor…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "bg-[#1a1a1a] text-white" : "text-neutral-700 hover:bg-neutral-100"
    }`;

  return (
    <main className="bg-neutral-50 min-h-screen pt-24 pb-16">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6 gap-3">
          <div>
            <div className="text-[11px] md:text-xs uppercase tracking-[0.24em] text-[#8F6E1E] font-semibold">CMS</div>
            <h1 className="text-2xl md:text-3xl font-black text-[#1a1a1a]">Yönetim Paneli</h1>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <span className="text-sm text-neutral-500 hidden md:inline">Hoş geldin, <b className="text-[#1a1a1a]">{user.username}</b></span>
            <button onClick={logout} className="inline-flex items-center gap-2 px-3 md:px-4 h-10 rounded-full bg-white border border-neutral-200 hover:bg-neutral-100 text-sm font-medium">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Çıkış</span>
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-4 lg:gap-6">
          <aside className="bg-white rounded-2xl border border-neutral-100 p-2 lg:p-3 h-max lg:sticky lg:top-24">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              <NavLink to="/admin" end className={linkCls}><LayoutDashboard className="w-4 h-4" /> <span className="whitespace-nowrap">Dashboard</span></NavLink>
              <NavLink to="/admin/news" className={linkCls}><Newspaper className="w-4 h-4" /> <span className="whitespace-nowrap">Haberler</span></NavLink>
              <NavLink to="/admin/sectors" className={linkCls}><Layers className="w-4 h-4" /> <span className="whitespace-nowrap">Sektörler</span></NavLink>
              <NavLink to="/admin/products" className={linkCls}><Package className="w-4 h-4" /> <span className="whitespace-nowrap">Ürünler</span></NavLink>
              <NavLink to="/admin/documents" className={linkCls}><FileText className="w-4 h-4" /> <span className="whitespace-nowrap">Dokümanlar</span></NavLink>
              <NavLink to="/admin/kurumsal" className={linkCls}><Building2 className="w-4 h-4" /> <span className="whitespace-nowrap">Kurumsal İçerik</span></NavLink>
              <NavLink to="/admin/hero" className={linkCls}><ImageIcon className="w-4 h-4" /> <span className="whitespace-nowrap">Galeri</span></NavLink>
              <NavLink to="/admin/contacts" className={linkCls}><Mail className="w-4 h-4" /> <span className="whitespace-nowrap">Form Mesajları</span></NavLink>
              <div className="hidden lg:block my-2 h-px bg-neutral-100" />
              <NavLink to="/admin/settings" className={linkCls}><SettingsIcon className="w-4 h-4" /> <span className="whitespace-nowrap">Site Ayarları</span></NavLink>
              <NavLink to="/admin/password" className={linkCls}><KeyRound className="w-4 h-4" /> <span className="whitespace-nowrap">Şifre</span></NavLink>
            </nav>
          </aside>

          <div>
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="news" element={<NewsPanel />} />
              <Route path="sectors" element={<SectorsPanel />} />
              <Route path="products" element={<ProductsPanel />} />
              <Route path="documents" element={<DocumentsPanel />} />
              <Route path="kurumsal" element={<KurumsalPanel />} />
              <Route path="hero" element={<HeroPanel />} />
              <Route path="contacts" element={<ContactsPanel />} />
              <Route path="settings" element={<SiteSettingsPanel />} />
              <Route path="password" element={<PasswordPanel />} />
            </Routes>
          </div>
        </div>
      </div>
      <Toaster />
    </main>
  );
};

export default Admin;
